<script setup lang="ts">
import type { PropertyHighlightedInfosProps } from '.';
import type { HorizontalListItem } from '~/components/ui/horizontal-list';
import type { LocationClickedEvent } from '~/types/mixpanel';

import { cn } from '@/utils';
import { usePropertyContext } from '~/composables/usePropertyContext';
import { usePropertyValues } from '~/composables/usePropertyValues';

import { PropertyLocationInfo } from '~/components/PropertyLocationInfo';
import { HorizontalList } from '~/components/ui/horizontal-list';
import Tag from '~/components/ui/tag/Tag.vue';

import propertyHighlightedInfosLocale from './PropertyHighlightedInfos.locale.json';
import PropertyHighlightedInfosSkeleton from './PropertyHighlightedInfosSkeleton.vue';

const props = withDefaults(defineProps<PropertyHighlightedInfosProps>(), {
	priceClasses: ''
});

const emit = defineEmits<{
	'location-clicked': [LocationClickedEvent];
	'show-footer-exclusive-tag': [];
	'hide-footer-exclusive-tag': [];
	'show-sticky-pulse': [];
	'hide-sticky-pulse': [];
}>();

const { isLoading } = usePropertyContext();

const { t } = useI18n({
	useScope: 'local',
	messages: propertyHighlightedInfosLocale
});

const { formattedPrice, formattedCondoFee, formattedTax } = usePropertyValues();

const taxItems = computed<HorizontalListItem[]>(() => {
	const items: HorizontalListItem[] = [];

	if (formattedCondoFee.value) {
		items.push({
			label: t('condoFee'),
			value: formattedCondoFee.value,
			key: 'condoFee'
		});
	}

	if (formattedTax.value) {
		items.push({
			label: t('tax'),
			value: formattedTax.value,
			key: 'tax'
		});
	}

	return items;
});

const overviewSectionRef = useTemplateRef<HTMLElement | null>(
	'overviewSection'
);

useIntersectionObserver(
	overviewSectionRef,
	entries => {
		const entry = entries[0];
		if (!entry) return;

		if (!entry.isIntersecting) {
			emit('show-sticky-pulse');

			if (props.isExclusive) {
				emit('show-footer-exclusive-tag');
			}
		} else if (entry.isIntersecting) {
			emit('hide-sticky-pulse');
			emit('hide-footer-exclusive-tag');
		}
	},
	{
		rootMargin: '-60px'
	}
);

function handleLocationClicked({
	clickLink = false,
	clickMap = false
}: Partial<LocationClickedEvent>) {
	emit('location-clicked', { clickLink, clickMap });
}
</script>

<template>
	<PropertyHighlightedInfosSkeleton v-if="isLoading" :is-loading="true" />

	<div
		v-else
		id="overview"
		ref="overviewSection"
		class="flex flex-col items-start gap-px bg-white px-5 pb-3 md:self-stretch md:px-0 md:pb-5 lg:gap-px lg:flex-row lg:items-center lg:justify-between xl:flex-col xl:items-start xl:justify-start xl:pb-6"
		data-section-name="Visão geral"
	>
		<div
			class="flex w-full flex-col items-start gap-4 pt-5 md:flex-row lg:flex-col lg:gap-5 lg:pt-8 xl:flex-row"
		>
			<div
				class="flex w-full flex-col items-start lg:flex-row lg:items-center xl:flex-col xl:items-start xl:gap-0"
			>
				<div class="flex w-1/2 flex-row gap-3 lg:w-auto xl:w-full">
					<h3
						v-if="formattedPrice"
						:class="
							cn(
								'font-sans text-[24px] xl:text-[30px] font-semibold leading-[150%] text-black',
								priceClasses
							)
						"
					>
						{{ formattedPrice }}
					</h3>
					<Tag
						v-if="isExclusive"
						ref="overviewExclusiveTag"
						class="h-7 w-min shrink-0 self-center"
					>
						<template #icon>
							<IconPilarTriangleFilled class="size-3 self-center xl:size-4" />
						</template>
						Exclusivo
					</Tag>
				</div>
				<HorizontalList
					v-if="taxItems.length > 0"
					:items="taxItems"
					class="lg:mt-0 lg:justify-end xl:mt-2 xl:justify-start"
				/>
			</div>
			<PropertyLocationInfo
				v-if="propertyType && region && city && state"
				:property-type="propertyType"
				:region="region"
				:city="city"
				:state="state"
				:is-loading="isLoading"
				class="w-full basis-[max-content] flex-row items-end md:flex-row-reverse lg:basis-auto lg:flex-row xl:basis-[max-content] xl:flex-row-reverse"
				@location-clicked="handleLocationClicked"
			/>
		</div>
	</div>
</template>
